import "./styles.css";
import React, { useState, useEffect, useMemo } from "react";
import { FaUserAlt, FaIdCard, FaCheckCircle, FaExclamationCircle, FaClock, FaMoneyBill, FaTruck } from 'react-icons/fa';
import { IoMdCheckboxOutline } from "react-icons/io";
import { PiCityBold } from "react-icons/pi";
import { TbDeviceIpadExclamation } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';
import { format, toZonedTime } from 'date-fns-tz';
import { addHours } from 'date-fns';
import ProgressBar from '../ProgressBar';

const ListaVisita = ({ listaVisitas = [], idmotorista, maxHeight }) => {
  const navigate = useNavigate();

  const [intervalos, setIntervalos] = useState({});

  const formatarDataHora = (datahora) => {
    if (!datahora) return '-';
    const timeZone = 'America/Sao_Paulo';
    const dataZoned = toZonedTime(datahora, timeZone);
    const dataSomada = addHours(dataZoned, 0);
    const formato = 'dd/MM/yyyy HH:mm:ss';
    return format(dataSomada, formato);
  };

  const getCardStyle = (situacaovisita) => {
    switch (situacaovisita) {
      case 0: return { backgroundColor: '#666' };
      case 1: return { backgroundColor: '#0a5c0a' };
      case 2: return { backgroundColor: '#931010' };
      case 3: return { backgroundColor: '#c3c366' };
      default: return { backgroundColor: 'rgb(3, 41, 106)' };
    }
  };

  const getFirstMovDate = (movimentos) => {
    if (!movimentos || movimentos.length === 0) return null;
    let min = null;
    for (const m of movimentos) {
      if (!m || !m.datahora) continue;
      const d = new Date(m.datahora);
      if (isNaN(d)) continue;
      if (min === null || d < min) min = d;
    }
    return min;
  };

  const getLastMovDate = (movimentos) => {
    if (!movimentos || movimentos.length === 0) return null;
    let max = null;
    for (const m of movimentos) {
      if (!m || !m.datahora) continue;
      const d = new Date(m.datahora);
      if (isNaN(d)) continue;
      if (max === null || d > max) max = d;
    }
    return max;
  };

  const sortedVisitas = useMemo(() => {
    return [...listaVisitas]
      .map((v, idx) => ({ ...v, originalIndex: idx }))
      .sort((a, b) => {
        const dataA = getFirstMovDate(a.movimentos);
        const dataB = getFirstMovDate(b.movimentos);

        if (dataA && dataB) return dataA.getTime() - dataB.getTime(); // crescente
        if (dataA && !dataB) return -1;
        if (!dataA && dataB) return 1;
        return a.originalIndex - b.originalIndex;
      });
  }, [listaVisitas]);


  const calcularIntervalos = (visitasOrdenadas) => {
    const res = {};
    if (!visitasOrdenadas || visitasOrdenadas.length === 0) return res;

    for (let i = 0; i < visitasOrdenadas.length; i++) {
      const atual = visitasOrdenadas[i];
      const proximo = visitasOrdenadas[i + 1]; 

      if (!proximo) {
        res[atual.IDvisita] = "-";
        continue;
      }

      const ultimoAtual = getLastMovDate(atual.movimentos);
      const primeiroProximo = getFirstMovDate(proximo.movimentos);

      if (!ultimoAtual || !primeiroProximo) {
        res[atual.IDvisita] = "-";
      } else {
        const diffMs = primeiroProximo.getTime() - ultimoAtual.getTime();
        
        if (isNaN(diffMs) || diffMs <= 0) {
          res[atual.IDvisita] = "-";
        } else {
          const horas = Math.floor(diffMs / (1000 * 60 * 60));
          const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

          // 👇 Se for 0 horas e 0 minutos, não mostra
          if (horas === 0 && minutos === 0) {
            res[atual.IDvisita] = "-";
          } else {
            res[atual.IDvisita] = `${String(horas).padStart(2, '0')} Horas e ${String(minutos).padStart(2, '0')} Minutos`;
          }
        }
      }
    }
    return res;
  };



  useEffect(() => {
    if (sortedVisitas.length > 0) {
      setIntervalos(calcularIntervalos(sortedVisitas));
    } else {
      setIntervalos({});
    }
  }, [sortedVisitas]);

  const handleCardClick = (IDmotorista, IDvisita) => {
    navigate(`/Visita/${IDmotorista}/${IDvisita}`);
  };

  return (
    <div className="card-container" style={{ maxHeight: maxHeight || '58vh', overflowY: 'auto', padding: '10px' }}>
      {sortedVisitas.map((visita) => (
        <React.Fragment key={visita.IDvisita}>
          <div
            className="card"
            style={getCardStyle(visita.situacaovisita)}
            onClick={() => handleCardClick(idmotorista, visita.IDvisita)}
          >

            <div className="card-content">
              <h3 style={{ width: '100%', textAlign: 'start', paddingLeft: '5%' }}>
                <FaUserAlt style={{ marginRight: '8px' }} />
                Cliente: {visita.IDcliente} - {visita.nomecliente} &nbsp;
                <br/> &nbsp; &nbsp; &nbsp; Visita nº: {visita.IDvisita} &nbsp;
                {visita.situacaovisita === 1 && <FaCheckCircle style={{ color: '#00cc44' }} />}
              </h3>
              <div className="row">
                <div className="col-sm-5" style={{ paddingLeft: '8%', textAlign: 'start', fontSize: '0.8em' }}>
                  <>
                    <FaIdCard style={{ marginRight: '8px' }} />
                    {visita.enderecocliente}
                  </>
                  <br />
                  <>
                    <PiCityBold style={{ marginRight: '8px', marginLeft: '0px' }} />
                    {visita.cidadecliente}
                  </>
                  <br />
                  <>
                    <FaMoneyBill style={{ marginRight: '8px', marginLeft: '0px' }} />
                    R$ {visita.valor}
                    &nbsp; &nbsp; &nbsp; <FaMoneyBill style={{ marginRight: '8px', marginLeft: '0px' }} />
                    Plano Pagamento: {visita.planopag}
                  </>
                  <br />
                  {visita.situacaovisita === 1 && (
                    <>
                      <FaClock style={{ marginRight: '8px', marginLeft: '0px' }} />
                      <b>Tempo Gasto: {visita.tempo} min.</b>
                      <br />
                    </>
                  )}
                  <>
                    <TbDeviceIpadExclamation style={{ marginRight: '8px', marginLeft: '0px' }} />
                    Obs: {visita.observacaovisita ? visita.observacaovisita : '-'}
                  </>
                </div>
                <div className="col-sm-6">
                  {(() => {
                    // Pegando as datas dos movimentos importantes
                    const mov1 = visita.movimentos?.find(m => m.tipo === 1);
                    const mov2 = visita.movimentos?.find(m => m.tipo === 2);
                    const mov3 = visita.movimentos?.find(m => m.tipo === 3);

                    // Função para calcular diferença
                    const calcDiff = (start, end) => {
                      if (!start || !end) return "-";
                      const diffMs = new Date(end).getTime() - new Date(start).getTime();
                      if (diffMs <= 0) return "-";
                      const horas = Math.floor(diffMs / (1000 * 60 * 60));
                      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                      return `${String(horas).padStart(2, "0")}h ${String(minutos).padStart(2, "0")}m`;
                    };

                    const tempoEntrega = calcDiff(mov2?.datahora, mov3?.datahora);
                    const tempoTotal = calcDiff(mov1?.datahora, mov3?.datahora);

                    return (
                      <>
                        {visita.movimentos?.map((mov) => (
                          <div className="row" key={mov.id}>
                            <div className="col-sm-12" align="left" style={{ fontSize: '14px' }}>
                              {(() => {
                                switch (mov.tipo) {
                                  case 1:
                                    return (<>
                                      <IoMdCheckboxOutline style={{ marginRight: '8px', marginLeft: '5%', color: '#00cc44', fontSize: '25px', fontWeight: 'bold' }} />
                                      Chegou no local.. &nbsp;&nbsp;&nbsp;
                                    </>);
                                  case 2:
                                    return (<>
                                      <IoMdCheckboxOutline style={{ marginRight: '8px', marginLeft: '5%', color: '#00cc44', fontSize: '25px', fontWeight: 'bold' }} />
                                      Começou entrega. &nbsp;
                                    </>);
                                  case 3:
                                    return (<>
                                      <IoMdCheckboxOutline style={{ marginRight: '8px', marginLeft: '5%', color: '#00cc44', fontSize: '25px', fontWeight: 'bold' }} />
                                      Colheu assinatura. &nbsp;
                                    </>);
                                  case 4:
                                    return (<>
                                      <IoMdCheckboxOutline style={{ marginRight: '8px', marginLeft: '5%', color: '#00cc44', fontSize: '25px', fontWeight: 'bold' }} />
                                      Finalizou entrega. &nbsp;
                                    </>);
                                  case 5:
                                    return (<>
                                      <FaExclamationCircle style={{ marginRight: '8px', marginLeft: '5%', color: 'red', fontSize: '18px', fontWeight: 'bold' }} />&nbsp;
                                      Ocorrência. &nbsp;&nbsp;&nbsp;
                                    </>);
                                  default:
                                    return mov.tipo;
                                }
                              })()}
                              <FaIdCard style={{ marginRight: '8px', marginLeft: '25px' }} />
                              {formatarDataHora(mov.datahora)}
                            </div>
                          </div>
                        ))}

                        {/* Exibe os tempos calculados se houver dados */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginTop: '5px' }}>
                          {tempoEntrega !== "-" && (
                            <div style={{ fontSize: "12px", fontWeight: "bold", color: "white", display: 'flex', alignItems: 'center' }}>
                              <FaClock style={{ marginRight: '5px', color: 'white' }} />
                              Tempo de entrega:&nbsp; {tempoEntrega}
                            </div>
                          )}
                          {tempoTotal !== "-" && (
                            <div style={{ fontSize: "12px", fontWeight: "bold", color: "white", display: 'flex', alignItems: 'center' }}>
                              <FaClock style={{ marginRight: '5px', color: 'white' }} />
                              Tempo total:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 
                              {tempoTotal}
                            </div>
                          )}
                        </div>

                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {intervalos[visita.IDvisita] &&
            intervalos[visita.IDvisita] !== "-" && (
            <p style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px", color: "#3c3c3c", width: "100%" }}>
              <FaTruck style={{ marginRight: '8px' }} /> {intervalos[visita.IDvisita]}
            </p>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ListaVisita;
